// Super-admin "view the app as another church" switcher — docs/FIX-PLANS.md
// §1e, docs/designs/SuperAdminSwitcher.png. Purely a client-side view: picking
// a church here only ever calls onSelect(churchId), which the caller wires to
// setActiveChurchId — never a church_id membership change, never a reload.
//
// Three pieces, all exported, used together from This Week's header and the
// Leaders dashboard:
//   ChurchSwitcherPill   — the "[church] ▾" trigger, replaces plain text
//   ViewingAsStrip       — the purple "Viewing as X · Back to Y" banner
//   ChurchSwitcherSheet  — the bottom sheet itself (default export)

import { useMemo, useState } from "react";
import { CircleLogoOrInitials } from "./ExploreTab";

const SANS = "'Plus Jakarta Sans', system-ui, sans-serif";

export function ChurchSwitcherPill({ church, onClick }) {
  if (!church) return null;
  return (
    <button type="button" onClick={onClick}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "var(--white)", border: "none", borderRadius: 999, padding: "4px 10px 4px 4px", fontFamily: SANS, fontSize: 13, fontWeight: 700, color: "var(--ink)", cursor: "pointer" }}>
      <span style={{ width: 18, height: 18, borderRadius: 9, overflow: "hidden", flexShrink: 0, background: "var(--lavender)", color: "var(--lavender-ink)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 800 }}>
        <CircleLogoOrInitials url={church.logo_url} name={church.name}/>
      </span>
      {church.name}
      <span style={{ fontSize: 10, opacity: 0.7 }}>▾</span>
    </button>
  );
}

export function ViewingAsStrip({ churchName, homeName, onBack }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, background: "#7C6BC4", color: "#fff", borderRadius: 16, padding: "12px 14px", margin: "10px 22px 0", fontFamily: SANS }}>
      <span style={{ fontSize: 13.5, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Viewing as {churchName}</span>
      <button type="button" onClick={onBack}
        style={{ flexShrink: 0, background: "rgba(255,255,255,0.22)", border: "none", borderRadius: 999, color: "#fff", fontSize: 12.5, fontWeight: 800, padding: "7px 13px", cursor: "pointer", fontFamily: SANS, whiteSpace: "nowrap" }}>
        Back to {homeName}
      </button>
    </div>
  );
}

export default function ChurchSwitcherSheet({ churches, activeChurchId, homeChurchId, onSelect, onClose }) {
  const [search, setSearch] = useState("");

  const pool = useMemo(() => {
    const list = churches || [];
    const home = list.find(c => c.id === homeChurchId);
    const others = list.filter(c => c.id !== homeChurchId).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return home ? [home, ...others] : others;
  }, [churches, homeChurchId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter(c => (c.name || "").toLowerCase().includes(q) || (c.location || "").toLowerCase().includes(q));
  }, [pool, search]);

  return (
    <>
      <div className="overlay" onClick={onClose}/>
      <div className="sheet">
        <div className="shandle"/>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "4px 20px 2px" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--gold-ink)" }}>Super admin</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "var(--ink)", marginTop: 2 }}>View the app as…</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 17, background: "rgba(31,27,22,0.06)", border: "none", color: "var(--stone)", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>✕</button>
        </div>
        <div className="shbody" style={{ paddingTop: 14 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${pool.length} churches`}
            style={{ width: "100%", boxSizing: "border-box", background: "var(--white)", border: "1px solid var(--border-strong)", borderRadius: 10, padding: "11px 14px", fontSize: 14, fontFamily: SANS, color: "var(--ink)", outline: "none", marginBottom: 6 }}/>
          {filtered.map((c, i) => {
            const isHome = c.id === homeChurchId;
            const isCurrent = c.id === activeChurchId;
            return (
              <button key={c.id} type="button" onClick={() => onSelect(c.id)}
                style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", background: "none", border: "none", borderTop: i > 0 ? "1px solid var(--border)" : "none", cursor: "pointer", padding: "12px 2px", fontFamily: SANS }}>
                <span style={{ width: 40, height: 40, borderRadius: 20, overflow: "hidden", flexShrink: 0, background: "var(--lavender)", color: "var(--lavender-ink)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800 }}>
                  <CircleLogoOrInitials url={c.logo_url} name={c.name}/>
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "var(--ink)" }}>{c.name}</div>
                  <div style={{ fontSize: 12.5, color: "var(--stone)", marginTop: 1 }}>{[c.location, isHome ? "your home church" : null].filter(Boolean).join(" · ")}</div>
                </div>
                {isCurrent && <span style={{ flexShrink: 0, fontSize: 16, color: "var(--ink)", fontWeight: 800 }}>✓</span>}
              </button>
            );
          })}
          <div style={{ fontSize: 12, color: "var(--stone)", lineHeight: 1.4, marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
            Only you see this. Members always see their own church.
          </div>
        </div>
      </div>
    </>
  );
}
